import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {Panel} from '@Components/panel';
import {DownloadPanelFooter} from '@Components/download-panel/components/download-panel-footer/download-panel-footer';
import styles from '@Components/download-panel/download-panel.module.scss';
import {FileTypeOption} from '@Components/download-panel/components/file-type-option/file-type-option';
import {DownloadMoreOptions} from '@Components/download-panel/components/download-more-options/download-more-options';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import {useDownloadPanelData} from '@Components/download-panel/download-panel.context';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import type {BaseDownloadAPIData, DownloadCallbackResponse, DownloadCheckoutAJAXResponseOrderSuccess, ProductsResponse} from '@Libraries/poster-download-library';
import {MAX_PAGES_ALLOWED_FOR_CHEAP_PRODUCT} from '@Libraries/poster-download-library';
import {
  CHEAP_PRODUCT_ID,
  doesProductRequirePayment,
  DownloadDialogSource,
  type DownloadPosterAPIData,
  DownloadPublishMode,
  EXPENSIVE_PRODUCT_ID,
  FREEBIE_PRODUCT_ID,
  getProductsForPoster,
  isDurationEnabledForGif,
  onDownloadFreePosterForPaygUser,
  onDownloadPoster,
  onDownloadPosterForFreebie,
  onDownloadPosterUnpurchased,
  onDownloadPosterWithOptions,
  openCheckoutDialogForTrialPremiumUsers,
  shouldShowDownloadUpsellingForUser,
  shouldShowFirstTimeAiChatUpselling,
  triggerBrowserDownload,
  VideoDesignSizeOptionType,
  processIAPDownloadPurchase,
  getCurrencyCodeForProduct,
} from '@Libraries/poster-download-library';
import {FileTypeOptions} from '@Components/download-panel/components/file-type-option/file-type-option.types';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {
  addOrderStateNew,
  addToCurrentProcessingIds,
  addToDropboxProcessingIds,
  clearDropboxProcessingIds,
  DownloadStateTypes,
  setCurrentViewingOrderIdKey,
  setIsViewingDropboxDownloads,
  setUpsellingRecommendationNumberIfNeeded,
  updateExternalModalOpenStateForDownloadPanel,
  updateDownloadLoadingPopupOpenState,
  updateSliderDragState,
  type OrderDownloadStateNew,
} from '@Components/download-panel/download-panel.reducer';
import {DownloadPanelDownloadingSection} from '@Components/download-panel/components/download-panel-downloading-section/download-panel-downloading-section';
import {isPremiumUser} from '@Utils/user.util';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {DownloadPanelUpsellingWrapper} from '@Components/download-panel/components/download-panel-upselling-wrapper/download-panel-upselling-wrapper';
import {DownloadPanelPublishingSectionWrapper} from '@Components/download-panel/components/download-panel-publishing-section-wrapper/download-panel-publishing-section-wrapper';
import {openGoogleDriveFolderPicker, saveToGoogleDriveWithSelectedFolder} from '@Libraries/google-drive-download-picker';
import {getStateFromUrl} from '@Utils/url.util';
import {isIOSApp, isIOSAppOrEmbeddedEditor} from '@Utils/browser.util';
import {showEditorNotificationMessageGrowl} from '@Components/poster-editor/library/poster-editor-library';
import {makeFilenameSafe} from '@Utils/file.util';
import {LetterSizeOptionType} from '@Components/download-panel/components/download-adjust-letter-size-option/download-adjust-letter-size-option.types';
import {AI_CHAT_UPSELLING_INDEX, ENABLED_DOWNLOAD_UPSELLING_DIALOG_NUMBER} from '@Components/download-panel/components/download-panel-upselling/download-panel-upselling.types';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {FeatureDemo} from '@Components/poster-editor/poster-editor.types';
import {updateFeatureDemo} from '@Components/poster-editor/poster-editor-reducer';
import {DOWNLOAD_GIF_DEFAULT_DURATION, DOWNLOAD_GIF_MAX_DURATION} from '@Components/download-panel/components/download-gif-duration-option/download-gif-duration-option.types';
import {DownloadGettyRequirementInfoBox} from '@Components/download-panel/components/download-getty-requirement-info-box/download-getty-requirement-info-box';
import {closeModal, updateModalDisableOutsideClickDismiss} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';
import {DownloadPanelInfoBox} from '@Components/download-panel/components/download-panel-info-box/download-panel-info-box';
import {DownloadDesignSizeOptionWrapper} from '@Components/download-panel/components/download-design-size-option-wrapper/download-design-size-option-wrapper';
import {DropboxDownloadSection} from '@Components/download-panel/components/dropbox-download-section/dropbox-download-section';
import {getAssetUrl} from '@Utils/s3.util';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {MOBILE_MODAL_WIDTH_THRESHOLD} from '@Components/modal';
import {getUniqueId} from '@Utils/string.util';
import {DownloadQualityOption} from '@Components/download-panel/components/download-quality-option/download-quality-option';
import {getUserId, getUserPremiumLevel, isUserLoggedIn, isUserPAYG} from '@Libraries/user.library';
import type {AJAXResponsePurchaseProduct} from '@Panels/poster-publishing-options-panel/components/publishing-embed-in-webpage-panel/publishing-embed-in-webpage-panel.types';
import {openModal} from '@Components/modal-container-deprecated';
import {openAuthenticateModal} from '@Modals/authenticate-modal/authenticate-modal-library';
import type {PosterLoadObject} from '@PosterWhiteboard/poster/poster.types';
import {DownloadSelectPagesOption} from '@Components/download-panel/components/download-select-pages-option/download-select-pages-option';
import {DownloadPaygPagesUpselling} from '@Components/download-panel/components/download-payg-pages-upselling/download-payg-pages-upselling';
import {logClientError} from '@Libraries/log.library';
import {DownloadPanelHeader} from '@Components/download-panel/components/download-panel-header/download-panel-header';
import {doesMultipagePosterHavePagesAsTemplates, isExpensiveVideoPoster} from '@Libraries/poster-object.library';

interface DownloadPanelContentManagerProps {
  id: string;
  className?: string;
  isOpenedInModal?: boolean;
  isOpenedInEditor?: boolean;
  isOpenedInPrintShop?: boolean;
}

export function DownloadPanelContentManager({
  className = '',
  isOpenedInPrintShop = false,
  isOpenedInEditor = true,
  isOpenedInModal = false,
  ...props
}: DownloadPanelContentManagerProps): ReactElement {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [bottomScrollInitializer, setBottomScrollInitializer] = useState<boolean>(false);
  const {bottomShadow} = useTopBottomScrollShadow(containerRef, bottomScrollInitializer);
  const freshPosterObjectRef = useRef<PosterLoadObject | null>(null);

  const {
    scale,
    selectedPages,
    needsPurchase,
    wasPreviouslyPurchased,
    posterObject,
    selectedProductId,
    transparency,
    posterVersionId,
    meetsGettyRequirements,
    productsData,
    appleDownloadProducts,
    margin,
    bleed,
    duration,
    videoScale,
    bleedUnit,
    isPDFWebQuality,
    colorProfile,
    letterSize,
    speed,
    fileType,
    downloadPageOptions,
    setProductsData,
  } = useDownloadPanelData();
  const SELECTED_PRODUCT = productsData?.find((productsData) => productsData.id === String(selectedProductId));
  const dispatch = useAppDispatch();
  const isMyStuffWebModal = isOpenedInModal && !isOpenedInEditor && !isOpenedInPrintShop && window.innerWidth > MOBILE_MODAL_WIDTH_THRESHOLD;
  const orderStateHashmapNew = useAppSelector((state) => {
    return state.downloadDialog.orderStateHashmapNew;
  });
  const currentlyProcessingOrderIdKeys = useAppSelector((state) => {
    return state.downloadDialog.currentlyProcessingOrderIdKeys;
  });
  const dropboxProcessingOrderIdKeys = useAppSelector((state) => {
    return state.downloadDialog.dropboxProcessingOrderIdKeys;
  });
  const [hasClosedUpsellingInThisSession, setHasClosedUpsellingInThisSession] = useState(false);
  const currentViewingOrderIdKey = useAppSelector((state) => state.downloadDialog.currentViewingOrderIdKey);
  const isViewingDropboxDownloads = useAppSelector((state) => state.downloadDialog.isViewingDropboxDownloads);
  const allOrders = currentlyProcessingOrderIdKeys.map((key) => orderStateHashmapNew[key]).filter((order) => order && !order.isDropboxDownload);
  const hasDownloadsInProgress = allOrders.some(
    (order) => order.state === DownloadStateTypes.DOWNLOADING || order.state === DownloadStateTypes.READY || order.state === DownloadStateTypes.ERROR
  );
  const currentViewingOrder = currentViewingOrderIdKey ? orderStateHashmapNew[currentViewingOrderIdKey] : undefined;

  const isDownloadInProgressOrReady = allOrders.some((order) => order.state === DownloadStateTypes.DOWNLOADING || order.state === DownloadStateTypes.READY);
  const [isInitialDownloadInProgress, setIsInitialDownloadInProgress] = useState(false);
  const onClose = useDefaultPanelClose(props.id, isOpenedInModal);
  const oauthUrl = useRef('');
  const state = useRef('');

  useEffect((): void => {
    setBottomScrollInitializer(!bottomScrollInitializer);
  }, [fileType]);

  useEffect(() => {
    const number = shouldShowFirstTimeAiChatUpselling() ? AI_CHAT_UPSELLING_INDEX : Math.floor(Math.random() * ENABLED_DOWNLOAD_UPSELLING_DIALOG_NUMBER);
    dispatch(setUpsellingRecommendationNumberIfNeeded(number));
  }, []);

  const toggleModalOverlayClick = (toggle: boolean): void => {
    if (isOpenedInModal) {
      dispatch(
        updateModalDisableOutsideClickDismiss({
          modalId: MODALS_IDS.DOWNLOAD_MODAL,
          disableOutsideClickDismiss: toggle,
        })
      );
      return;
    }

    if (isOpenedInEditor) {
      dispatch(updateSliderDragState(toggle));
    }
  };

  const onSlide = (): void => {
    toggleModalOverlayClick(true);
  };
  const onSlideEnded = (): void => {
    setTimeout((): void => {
      toggleModalOverlayClick(false);
    }, 300);
  };

  const onDownloadUpdatedDesign = (): void => {
    void onClickDownload(true);
  };

  const getAnimatingDownloadIcon = (): ReactElement => {
    return (
      <svg className={`spacing-m-r-2 _overflow-visible`} xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
        <path
          className={styles.arrowHead}
          d="M4.85992 12.9839C5.04942 12.7944 5.35642 12.7944 5.54542 12.9839L7.78842 15.2269L10.0314 12.9839C10.2209 12.7944 10.5279 12.7944 10.7169 12.9839C10.9059 13.1734 10.9064 13.4804 10.7169 13.6694L8.13092 16.2554C8.03992 16.3464 7.91692 16.3974 7.78792 16.3974C7.65892 16.3974 7.53592 16.3464 7.44492 16.2554L4.85892 13.6694C4.66942 13.4799 4.66942 13.1729 4.85892 12.9839H4.85992Z"
          fill="#403F5D"
        />
        <path
          className={styles.arrowLine}
          d="M7.78969 9.60938C8.05769 9.60938 8.27469 9.82638 8.27469 10.0944V15.9124C8.27469 16.1799 8.05769 16.3974 7.78969 16.3974C7.52169 16.3974 7.30469 16.1804 7.30469 15.9124V10.0944C7.30469 9.82638 7.52169 9.60938 7.78969 9.60938Z"
          fill="#403F5D"
        />
        <path
          d="M5.64062 1.45292C6.49563 1.42192 7.34663 1.58592 8.12913 1.93142C8.91163 2.27692 9.60613 2.79592 10.1591 3.44842C10.6301 4.00442 10.9891 4.64542 11.2161 5.33542H11.6671L5.64062 1.45292Z"
          fill="#403F5D"
        />
        <path
          d="M5.62926 1.45697C6.48376 1.42547 7.33426 1.58797 8.11676 1.93247C8.89926 2.27697 9.59376 2.79447 10.1473 3.44597C10.6193 4.00097 10.9783 4.64097 11.2068 5.33047H11.6588C12.4848 5.33097 13.2873 5.60647 13.9388 6.11347C14.5903 6.62047 15.0553 7.33047 15.2588 8.13047C15.4623 8.93047 15.3938 9.77597 15.0638 10.5335C14.7338 11.2905 14.1608 11.916 13.4358 12.3115C13.2008 12.4395 12.9063 12.353 12.7778 12.118C12.6493 11.883 12.7363 11.5885 12.9713 11.46C13.5073 11.168 13.9308 10.7055 14.1748 10.146C14.4188 9.58647 14.4693 8.96147 14.3188 8.36997C14.1683 7.77847 13.8248 7.25397 13.3433 6.87897C12.8618 6.50397 12.2688 6.30047 11.6583 6.29997L5.62926 1.45697ZM11.6593 6.29997H10.8448C10.6238 6.29997 10.4308 6.15047 10.3753 5.93647C10.1978 5.25047 9.86726 4.61397 9.40876 4.07397C8.95026 3.53397 8.37476 3.10547 7.72626 2.81997C7.07776 2.53447 6.37326 2.39997 5.66526 2.42597C4.95726 2.45197 4.26426 2.63847 3.63876 2.97047C3.01326 3.30247 2.47076 3.77297 2.05326 4.34497C1.63526 4.91697 1.35276 5.57647 1.22626 6.27347C1.09976 6.97047 1.13326 7.68747 1.32376 8.36947C1.51426 9.05147 1.85676 9.68197 2.32626 10.213C2.50376 10.4135 2.48476 10.72 2.28376 10.8975C2.08276 11.075 1.77676 11.056 1.59926 10.8555C1.03326 10.215 0.619263 9.45397 0.389763 8.63047C0.160263 7.80697 0.119763 6.94197 0.272263 6.10047C0.424763 5.25897 0.766263 4.46347 1.27026 3.77297C1.77426 3.08247 2.42876 2.51547 3.18376 2.11447C3.93876 1.71347 4.77526 1.48847 5.62926 1.45697L11.6593 6.29997Z"
          fill="#403F5D"
        />
      </svg>
    );
  };

  const getFreeEditsUsedUpBanner = (): ReactElement => {
    return (
      <div className={`${styles.freeEditsContainer} flex-row-justify-center flex-items-center text-center spacing-p-t-2 spacing-p-b-2 spacing-p-l-3 spacing-p-r-3`}>
        <Icon className={`content-link ${styles.marginRightSmall}`} icon="icon-info" size={IconSize.SIZE_ICON_16} />
        <Text className="content-link" val={window.i18next.t('pmwjs_free_edits_used_up')} size={TextSize.XSMALL} />
      </div>
    );
  };

  const getDownloadBanner = (): ReactElement | null => {
    if (isMyStuffWebModal) {
      return null;
    }

    const allOrders = currentlyProcessingOrderIdKeys.map((key) => orderStateHashmapNew[key]).filter((order) => order && !order.isDropboxDownload);

    const downloadingOrders = allOrders.filter((order) => order.state === DownloadStateTypes.DOWNLOADING);
    const completedOrders = allOrders.filter((order) => order.state === DownloadStateTypes.READY);
    const errorOrders = allOrders.filter((order) => order.state === DownloadStateTypes.ERROR);
    const totalActive = downloadingOrders.length + completedOrders.length + errorOrders.length;

    const firstOrder = downloadingOrders[0] || completedOrders[0] || errorOrders[0] || allOrders[0];

    if (!firstOrder) {
      return null;
    }

    const completedCount = completedOrders.length;
    let text: string;
    if (totalActive === 1) {
      if (downloadingOrders.length > 0) {
        text = window.i18next.t('pmwjs_downloading');
      } else if (completedOrders.length > 0) {
        text = window.i18next.t('pmwjs_downloaded');
      } else if (errorOrders.length > 0) {
        text = window.i18next.t('pmwjs_something_went_wrong');
      } else {
        text = window.i18next.t('pmwjs_downloading');
      }
    } else {
      if (completedCount === totalActive) {
        text = `${window.i18next.t('pmwjs_downloaded')} (${completedCount} / ${totalActive})`;
      } else {
        text = `${window.i18next.t('pmwjs_downloading_plain_text')} (${completedCount} / ${totalActive})`;
      }
    }

    return (
      <div
        className={`_position-relative spacing-p-t-2 spacing-p-b-2 spacing-p-l-3 spacing-p-r-3 _cursor-pointer shadow-floating-elevation-1 ${styles.downloadBanner} hover-transition-all radius-8 flex-center`}
        onClick={(): void => {
          if (!isMyStuffWebModal) {
            dispatch(setCurrentViewingOrderIdKey(firstOrder.orderIdKey));
          }
        }}
      >
        <div className="flex flex-h-row flex-items-center">
          {downloadingOrders.length > 0 ? getAnimatingDownloadIcon() : <Icon icon="icon-cloud-download" size={IconSize.SIZE_ICON_16} className={styles.marginRight} />}
          <Text val={text} size={TextSize.XSMALL} bold className="content-body flex-1" />
        </div>
        <Icon icon="icon-caret-right" size={IconSize.SIZE_ICON_20} className={styles.bannerIcon} />
      </div>
    );
  };

  const getDropboxBanner = (): ReactElement | null => {
    const dropboxOrders = dropboxProcessingOrderIdKeys.map((key) => orderStateHashmapNew[key]).filter((order) => order?.isDropboxDownload);

    if (dropboxOrders.length === 0) {
      return null;
    }

    const downloadingCount = dropboxOrders.filter((order) => order.state === DownloadStateTypes.DOWNLOADING).length;
    const readyCount = dropboxOrders.filter((order) => order.state === DownloadStateTypes.READY).length;
    const totalCount = dropboxOrders.length;

    let text: string;
    if (downloadingCount > 0) {
      text = totalCount === 1 ? window.i18next.t('pmwjs_preparing_for_dropbox') : `${window.i18next.t('pmwjs_preparing_for_dropbox_plain')} (${readyCount}/${totalCount})`;
    } else {
      text = window.i18next.t('pmwjs_files_ready_for_dropbox');
    }

    return (
      <div
        className={`_position-relative spacing-p-t-2 spacing-p-b-2 spacing-p-l-3 spacing-p-r-3 _cursor-pointer shadow-floating-elevation-1 ${styles.dropboxBanner} hover-transition-all radius-8 flex-center`}
        onClick={(): void => {
          dispatch(setIsViewingDropboxDownloads(true));
        }}
      >
        <div className="flex flex-h-row flex-items-center">
          {downloadingCount > 0 ? (
            <CircularProgressLoader color={LOADER_COLOR.PRIMARY_DARK} size={LOADER_SIZE.SMALL} />
          ) : (
            <img className={styles.socialThumbs} src={getAssetUrl('dropbox-logo.png')} alt={window.i18next.t('pmwjs_save_to_dropbox')} />
          )}
          <Text val={text} size={TextSize.XSMALL} bold className="content-body flex-1 spacing-m-l-2" />
        </div>
        <Icon icon="icon-caret-right" size={IconSize.SIZE_ICON_20} className={styles.bannerIcon} />
      </div>
    );
  };

  const hasDropboxDownloadsInProgress = dropboxProcessingOrderIdKeys.length > 0;

  const getOptionsForType = (): ReactElement => {
    return (
      <>
        <div className={`spacing-p-t-3 spacing-p-b-3 spacing-p-r-4 spacing-p-l-4 flexbox flex-v-row gap-4`}>
          {needsPurchase && wasPreviouslyPurchased && !isOpenedInPrintShop ? getFreeEditsUsedUpBanner() : null}
          {hasDownloadsInProgress ? getDownloadBanner() : null}
          {hasDropboxDownloadsInProgress ? getDropboxBanner() : null}
          <DownloadPanelInfoBox onDownloadUpdatedDesignClick={onDownloadUpdatedDesign} />
          <DownloadGettyRequirementInfoBox />
          <FileTypeOption />
          {isOpenedInPrintShop ? null : <DownloadQualityOption />}
          <DownloadSelectPagesOption isOpenedInEditor={isOpenedInEditor} />
          <DownloadPaygPagesUpselling />
          <DownloadDesignSizeOptionWrapper onSlideCallback={onSlide} onSlideEndedCallback={onSlideEnded} />
        </div>
        <DownloadMoreOptions
          onSlideCallback={onSlide}
          onSlideEndedCallback={onSlideEnded}
          onToggleOpenClose={(): void => {
            setTimeout((): void => {
              setBottomScrollInitializer(!bottomScrollInitializer);
            }, 300);
          }}
        />
      </>
    );
  };

  const doShowUpselling = (): boolean => {
    if (isOpenedInModal || !isOpenedInEditor) {
      return false;
    }
    if (!isDownloadInProgressOrReady) {
      return false;
    }
    if (hasClosedUpsellingInThisSession) {
      return false;
    }
    if (shouldShowFirstTimeAiChatUpselling()) {
      return true;
    }
    return shouldShowDownloadUpsellingForUser();
  };

  const shouldShowUpselling = doShowUpselling();
  const isViewingDownloadsSection = !!currentViewingOrderIdKey || isViewingDropboxDownloads;

  const getPanelContentWrapper = (): ReactElement<any> => {
    return (
      <div ref={containerRef} className="_position-relative _full-height _overflow-auto">
        {getPanelContent()}
        <DownloadPanelUpsellingWrapper
          containerRef={containerRef}
          wrapperRef={wrapperRef}
          isVisible={shouldShowUpselling && isViewingDownloadsSection}
          onClose={(): void => {
            setHasClosedUpsellingInThisSession(true);
            setTimeout((): void => {
              dispatch(setUpsellingRecommendationNumberIfNeeded(null));
            }, 300);
          }}
        />
        <DownloadPanelPublishingSectionWrapper
          containerRef={containerRef}
          wrapperRef={wrapperRef}
          isVisible={!shouldShowUpselling && isViewingDownloadsSection && isOpenedInEditor && !isOpenedInModal && !hasClosedUpsellingInThisSession}
        />
      </div>
    );
  };

  const getPanelContent = (): ReactElement => {
    if (isViewingDropboxDownloads) {
      return (
        <DropboxDownloadSection
          isOpenedInModal={isOpenedInModal}
          onClickBack={(): void => {
            dispatch(setIsViewingDropboxDownloads(false));
          }}
          onAllFilesSaved={(): void => {
            dispatch(clearDropboxProcessingIds());
            dispatch(setIsViewingDropboxDownloads(false));
          }}
        />
      );
    }

    if (currentViewingOrderIdKey) {
      return (
        <DownloadPanelDownloadingSection
          orderIdKey={currentViewingOrderIdKey}
          hasClosedUpsellingInThisSession={hasClosedUpsellingInThisSession}
          onCloseUpselling={(): void => {
            setHasClosedUpsellingInThisSession(true);
          }}
          isOpenedInPrintShop={isOpenedInPrintShop}
          isOpenedInEditor={isOpenedInEditor}
          onClickBack={(): void => {
            dispatch(setCurrentViewingOrderIdKey(undefined));
          }}
          isOpenedInModal={isOpenedInModal}
        />
      );
    }

    return <div className={`_full-width _full-height ${styles.container}`}>{getOptionsForType()}</div>;
  };

  const getDataAccordingToType = (): DownloadPosterAPIData => {
    switch (fileType) {
      case FileTypeOptions.GIF:
        return {
          type: FileTypeOptions.GIF,
          downloadOptions: {
            speed: isDurationEnabledForGif(posterObject) ? speed : 1,
            scale: scale,
            duration: isDurationEnabledForGif(posterObject) ? Math.min(duration, DOWNLOAD_GIF_MAX_DURATION) : DOWNLOAD_GIF_DEFAULT_DURATION,
            pagesHashedIds: getSelectedPageHashedIds(),
          },
        };
      case FileTypeOptions.JPG:
        return {
          type: FileTypeOptions.JPG,
          downloadOptions: {
            bleedUnits: bleedUnit,
            bleedValue: bleed,
            scale: scale,
            pagesHashedIds: getSelectedPageHashedIds(),
          },
        };
      case FileTypeOptions.PNG:
        return {
          type: FileTypeOptions.PNG,
          downloadOptions: {
            bleedUnits: bleedUnit,
            isTransparent: transparency,
            bleedValue: bleed,
            scale: scale,
            pagesHashedIds: getSelectedPageHashedIds(),
          },
        };
      case FileTypeOptions.PDF:
        return {
          type: fileType,
          downloadOptions: {
            bleedUnits: bleedUnit,
            bleedValue: bleed,
            hasMargin: margin,
            colorProfile: colorProfile,
            letterSize: letterSize === LetterSizeOptionType.NO ? undefined : letterSize,
            isWebQuality: isPDFWebQuality,
            pagesHashedIds: getSelectedPageHashedIds(),
          },
        };
      case FileTypeOptions.MP4:
        return {
          type: FileTypeOptions.MP4,
          downloadOptions: {
            speed: speed,
            scale: scale,
            duration: duration,
            pagesHashedIds: getSelectedPageHashedIds(),
          },
        };
      case FileTypeOptions.WEBM:
        console.log('todo');
        break;
      case FileTypeOptions.WEBP:
        console.log('todo');
        break;
      default:
        throw new Error('File type not supported yet');
    }

    throw new Error('File type not supported yet');
  };

  const addToErrorState = (): void => {
    const defaultErrorOrderId = 'default-error-order-id';
    dispatch(addToCurrentProcessingIds(defaultErrorOrderId));
    dispatch(
      addOrderStateNew(
        buildOrderStatePayload({
          state: DownloadStateTypes.ERROR,
          orderId: defaultErrorOrderId,
          orderIdKey: defaultErrorOrderId,
          name: getCurrentPosterObject().name,
          fileType: fileType,
          thumbnailUrl: undefined,
        })
      )
    );
  };
  const getIsMultipageDownload = (): boolean => {
    const currentPosterObject = getCurrentPosterObject();
    return currentPosterObject.pages.pageOrder.length > 1 && selectedPages.length > 1;
  };

  const buildOrderStatePayload = (payload: Partial<OrderDownloadStateNew>): OrderDownloadStateNew => {
    return {
      ...payload,
      isMultipageDownload: getIsMultipageDownload(),
      selectedPages,
    } as OrderDownloadStateNew;
  };

  const addToDownloadState = (data: DownloadCallbackResponse): void => {
    const currentPosterObject = getCurrentPosterObject();

    dispatch(addToCurrentProcessingIds(data.idOrderKey));
    dispatch(
      addOrderStateNew(
        buildOrderStatePayload({
          state: DownloadStateTypes.DOWNLOADING,
          orderId: data.idOrder,
          orderIdKey: data.idOrderKey,
          downloadResult: data,
          name: currentPosterObject.name,
          fileType: fileType,
          thumbnailUrl: data.thumbnailUrl,
          isHeavyOrder: data.isHeavyOrder,
        })
      )
    );
  };

  const addToDownloadReadyState = (data: DownloadCallbackResponse, url: string): void => {
    const currentPosterObject = getCurrentPosterObject();
    const downloadSafePosterName = makeFilenameSafe(currentPosterObject.name, 'poster');
    triggerBrowserDownload(url, downloadSafePosterName, fileType);

    dispatch(addToCurrentProcessingIds(data.idOrderKey));
    dispatch(
      addOrderStateNew(
        buildOrderStatePayload({
          state: DownloadStateTypes.READY,
          orderId: data.idOrder,
          orderIdKey: data.idOrderKey,
          downloadURL: url,
          name: currentPosterObject.name,
          downloadResult: data,
          fileType: fileType,
          thumbnailUrl: data.thumbnailUrl,
        })
      )
    );
  };

  const getSelectedPageHashedIds = (): string[] | undefined => {
    const posterObject = getCurrentPosterObject();
    if (posterObject.pages.pageOrder.length === 1) {
      return undefined;
    }

    if (selectedPages && selectedPages.length > 0) {
      if (selectedPages.length === posterObject.pages.pageOrder.length) {
        return undefined;
      }

      const pageIdSet = new Set(posterObject.pages.pageOrder);
      const hashedIds = selectedPages.filter((pageId) => pageIdSet.has(pageId));

      return hashedIds.length > 0 ? hashedIds : undefined;
    }

    return undefined;
  };

  const getDataForDownload = (ignorePosterVersionIdIfPresent = false): BaseDownloadAPIData => {
    return {
      productId: isPremiumUser() ? getProductIdFromOptions() : getProductIdFromOptionsForNonPremiumUsers(),
      hashedId: getCurrentPosterObject().hashedID,
      isSilent: false,
      posterVersionId: ignorePosterVersionIdIfPresent ? undefined : (posterVersionId ?? undefined),
      isFromDownloadOptionsPage: isOpenedInPrintShop,
      orderId: isOpenedInPrintShop ? downloadPageOptions?.orderId : undefined,
    };
  };

  const triggerDownloadForPremiumUser = async (ignorePosterVersionIdIfPresent = false): Promise<void> => {
    setIsInitialDownloadInProgress(true);

    try {
      const data = await onDownloadPoster(getDataForDownload(ignorePosterVersionIdIfPresent), getDataAccordingToType());
      if (data.downloadUrl && data.downloadUrl !== '') {
        addToDownloadReadyState(data, data.downloadUrl);
        if (!isMyStuffWebModal) {
          dispatch(setCurrentViewingOrderIdKey(data.idOrderKey));
        } else {
          dispatch(closeModal(MODALS_IDS.DOWNLOAD_MODAL));
        }
        return;
      }
      addToDownloadState(data);
      if (!isMyStuffWebModal) {
        dispatch(setCurrentViewingOrderIdKey(data.idOrderKey));
      } else {
        dispatch(closeModal(MODALS_IDS.DOWNLOAD_MODAL));
      }
    } catch (error: unknown) {
      console.error('Download error:', error);
      addToErrorState();
    } finally {
      setIsInitialDownloadInProgress(false);
      void refreshProductsInContext();
    }
  };

  const triggerDownloadForFreebieUser = async (ignorePosterVersionIdIfPresent = false): Promise<void> => {
    setIsInitialDownloadInProgress(true);
    const currentPosterObject = getCurrentPosterObject();
    try {
      const data = await onDownloadPosterForFreebie(getDataForDownload(ignorePosterVersionIdIfPresent), getDataAccordingToType());
      const orderIdKey = getUniqueId();

      if (data.downloadUrl && data.downloadUrl !== '') {
        dispatch(addToCurrentProcessingIds(orderIdKey));
        if (!isMyStuffWebModal) {
          dispatch(setCurrentViewingOrderIdKey(orderIdKey));
        } else {
          dispatch(closeModal(MODALS_IDS.DOWNLOAD_MODAL));
        }
        dispatch(
          addOrderStateNew(
            buildOrderStatePayload({
              state: DownloadStateTypes.READY,
              orderId: orderIdKey,
              orderIdKey: orderIdKey,
              downloadURL: data.downloadUrl,
              name: currentPosterObject.name,
              fileType: fileType,
              thumbnailUrl: data.thumbnailUrl,
              isDownloadPageOrder: false,
              isFreebieDownloadPageOrder: true,
            })
          )
        );
        const downloadSafePosterName = makeFilenameSafe(currentPosterObject.name, 'poster');
        triggerBrowserDownload(data.downloadUrl, downloadSafePosterName, fileType);
        return;
      }

      if (data.generationKey) {
        dispatch(addToCurrentProcessingIds(orderIdKey));
        dispatch(
          addOrderStateNew(
            buildOrderStatePayload({
              state: DownloadStateTypes.DOWNLOADING,
              orderId: orderIdKey,
              orderIdKey: orderIdKey,
              name: currentPosterObject.name,
              fileType: fileType,
              generationKey: data.generationKey,
              thumbnailUrl: data.thumbnailUrl,
              isDownloadPageOrder: false,
              isFreebieDownloadPageOrder: true,
            })
          )
        );
        if (!isMyStuffWebModal) {
          dispatch(setCurrentViewingOrderIdKey(orderIdKey));
        } else {
          dispatch(closeModal(MODALS_IDS.DOWNLOAD_MODAL));
        }
        return;
      }

      addToErrorState();
    } catch (error: unknown) {
      console.error('Download error:', error);
      addToErrorState();
    } finally {
      setIsInitialDownloadInProgress(false);
      void refreshProductsInContext();
    }
  };

  const onCheckoutClose = (): void => {
    toggleExternalModalOpenState(false);
  };

  const createCheckoutSuccessHandler = (isGoogleDriveFlow: boolean, isDropboxFlow: boolean) => {
    return (data: DownloadCheckoutAJAXResponseOrderSuccess): void => {
      toggleExternalModalOpenState(false);
      if (!data.idOrder || !data.idOrderKey) {
        console.error('PAYG checkout success but missing order ID');
        return;
      }

      if (isDropboxFlow) {
        dispatch(addToDropboxProcessingIds(data.idOrderKey));
      }
      dispatch(addToCurrentProcessingIds(data.idOrderKey));
      dispatch(
        addOrderStateNew(
          buildOrderStatePayload({
            orderId: data.idOrder,
            orderIdKey: data.idOrderKey,
            state: DownloadStateTypes.DOWNLOADING,
            name: getCurrentPosterObject().name,
            fileType: fileType,
            isPaygDownload: true,
            thumbnailUrl: data.thumbnailUrl,
            isHeavyOrder: data.isHeavyOrder,
            isGoogleDriveDownload: isGoogleDriveFlow,
            isDropboxDownload: isDropboxFlow,
          })
        )
      );

      if (isDropboxFlow) {
        dispatch(setIsViewingDropboxDownloads(true));
      } else if (!isMyStuffWebModal) {
        dispatch(setCurrentViewingOrderIdKey(data.idOrderKey));
      } else {
        dispatch(closeModal(MODALS_IDS.DOWNLOAD_MODAL));
        dispatch(updateDownloadLoadingPopupOpenState(true));
      }

      void refreshProductsInContext();
      setIsInitialDownloadInProgress(false);
    };
  };

  const openCheckoutDialogForPAYGUsers = (response: AJAXResponsePurchaseProduct, isGoogleDriveFlow = false, isDropboxFlow = false): void => {
    toggleExternalModalOpenState(true);

    if (isIOSApp()) {
      if (!response.checkout.appleProductId || !response.checkout.id) {
        onCheckoutClose();
        addToErrorState();
        return;
      }
      void processIAPDownloadPurchase(response.checkout.id, response.checkout.appleProductId, createCheckoutSuccessHandler(isGoogleDriveFlow, isDropboxFlow), (showError) => {
        toggleExternalModalOpenState(false);
        setIsInitialDownloadInProgress(false);
        if (showError) {
          addToErrorState();
        }
      });
      return;
    }

    window.PMW.util.require(typeof window.PMW.stripeCheckout === 'undefined', 'stripeCheckoutDialog', true, function () {
      window.PMW.stripeCheckout.showStripeCheckoutForDownloadPurchase(
        response.checkout.priceId,
        Number(response.checkout.id),
        response.checkout.posterHash,
        Number(response.checkout.idProduct),
        false,
        createCheckoutSuccessHandler(isGoogleDriveFlow, isDropboxFlow),
        onCheckoutClose
      );
    });
  };

  const processCheckoutResponse = async (response: AJAXResponsePurchaseProduct): Promise<void> => {
    if (!response) {
      openModal(MODALS_IDS.ERROR_MODAL);
      return;
    }

    if (response.orderRegen) {
      await startFreeDownloadForPaygUser();
      return;
    }

    if (response.checkout) {
      openCheckoutDialogForPAYGUsers(response);
    }
  };

  const startFreeDownloadForPaygUser = async (ignorePosterVersionIdIfPresent = false): Promise<void> => {
    const currentPosterObject = getCurrentPosterObject();
    try {
      const data = await onDownloadFreePosterForPaygUser(getDataForDownload(ignorePosterVersionIdIfPresent), getDataAccordingToType());

      if (data.downloadUrl && data.idOrderKey) {
        const downloadSafePosterName = makeFilenameSafe(currentPosterObject.name, 'poster');
        triggerBrowserDownload(data.downloadUrl, downloadSafePosterName, fileType);

        dispatch(addToCurrentProcessingIds(data.idOrderKey));
        dispatch(
          addOrderStateNew(
            buildOrderStatePayload({
              orderId: data.idOrder ?? '',
              orderIdKey: data.idOrderKey,
              state: DownloadStateTypes.READY,
              downloadURL: data.downloadUrl,
              downloadResult: {
                ...data,
                printShopLink: data.printShopLink,
              },
              name: currentPosterObject.name,
              fileType: fileType,
              isPaygDownload: true,
              thumbnailUrl: data.thumbnailUrl,
            })
          )
        );
        if (!isMyStuffWebModal) {
          dispatch(setCurrentViewingOrderIdKey(data.idOrderKey));
        } else {
          dispatch(closeModal(MODALS_IDS.DOWNLOAD_MODAL));
        }
        setIsInitialDownloadInProgress(false);
        void refreshProductsInContext();
        return;
      }

      if (data.idOrder && data.idOrderKey) {
        dispatch(addToCurrentProcessingIds(data.idOrderKey));
        dispatch(
          addOrderStateNew(
            buildOrderStatePayload({
              orderId: data.idOrder,
              orderIdKey: data.idOrderKey,
              state: DownloadStateTypes.DOWNLOADING,
              name: getCurrentPosterObject().name,
              fileType: fileType,
              isPaygDownload: true,
              thumbnailUrl: data.thumbnailUrl,
              isHeavyOrder: data.isHeavyOrder,
              downloadResult: {
                ...data,
                printShopLink: data.printShopLink,
              },
            })
          )
        );
        if (!isMyStuffWebModal) {
          dispatch(setCurrentViewingOrderIdKey(data.idOrderKey));
        } else {
          dispatch(closeModal(MODALS_IDS.DOWNLOAD_MODAL));
        }
      }
    } catch (err: unknown) {
      console.error('Download error:', err);
      addToErrorState();
    } finally {
      setIsInitialDownloadInProgress(false);
      void refreshProductsInContext();
    }
  };

  const getProducts = async (): Promise<ProductsResponse> => {
    const updatedProductsPromise = getProductsForPoster(getCurrentPosterObject());
    return await updatedProductsPromise;
  };

  const isPaymentRequired = async (): Promise<{isUserPremium: boolean; isPaymentNeeded: boolean}> => {
    const updatedProducts = await getProducts();
    if (updatedProducts?.isUserPremium) {
      return {isUserPremium: true, isPaymentNeeded: false};
    }
    return {
      isUserPremium: false,
      isPaymentNeeded: doesProductRequirePayment(selectedProductId, updatedProducts.needsPurchase),
    };
  };

  const refreshProductsInContext = async (): Promise<void> => {
    try {
      const updatedProducts = await getProducts();
      setProductsData(updatedProducts);
    } catch (error) {
      console.error('Failed to refresh products:', error);
    }
  };

  const isDownloadAllowedForPoster = (): boolean => {
    const currentPosterObject = getCurrentPosterObject();
    const isUserTheOwnerOfPoster = String(getUserId()) === String(currentPosterObject.creator?.id);
    const isPosterGalleryTemplate = doesMultipagePosterHavePagesAsTemplates(currentPosterObject);

    if (!currentPosterObject.id) {
      void logClientError('DOWNLOAD_NOT_ALLOWED', {
        reason: 'Poster ID is missing',
        posterObjectId: currentPosterObject.id,
        windowPosterObjectId: isOpenedInEditor ? window.posterEditor?.whiteboard?.id : 'not in editor',
        isUserLoggedIn: isUserLoggedIn(),
      });
      return false;
    }

    return !(isPosterGalleryTemplate && !isUserTheOwnerOfPoster);
  };

  const startPaygDownloadFlow = async (ignorePosterVersionIdIfPresent = false): Promise<void> => {
    setIsInitialDownloadInProgress(true);
    const currentPosterObject = getCurrentPosterObject();

    const isDownloadAllowed = isDownloadAllowedForPoster();

    if (!isDownloadAllowed) {
      void logClientError('DOWNLOAD_NOT_ALLOWED', {
        posterObjectId: currentPosterObject.id,
        isUserLoggedIn: isUserLoggedIn(),
      });
      addToErrorState();
      return;
    }

    const {isUserPremium, isPaymentNeeded} = await isPaymentRequired();
    if (isUserPremium) {
      await triggerDownloadForPremiumUser(ignorePosterVersionIdIfPresent);
      return;
    }

    if (!isPaymentNeeded) {
      await startFreeDownloadForPaygUser(ignorePosterVersionIdIfPresent);
      return;
    }

    try {
      const data = await onDownloadPosterUnpurchased(
        {
          hashedId: currentPosterObject.hashedID,
          productId: selectedProductId,
          mode: DownloadPublishMode.DOWNLOAD,
          currencyCode: getCurrencyCodeForProduct(SELECTED_PRODUCT, appleDownloadProducts),
          promotion: '',
          isInAppPurchase: window.PMW.isIOSApp(),
        },
        getDataAccordingToType()
      );

      await processCheckoutResponse(data);
    } catch (error: unknown) {
      console.error('Download error:', error);
      addToErrorState();
    } finally {
      setIsInitialDownloadInProgress(false);
      void refreshProductsInContext();
    }
  };

  const trackQRDownloadDemoIfNeeded = (): void => {
    if (!isOpenedInEditor) {
      return;
    }
    const featureDemo = window.PMW.redux.store.getState().posterEditor.featureDemo;
    if (featureDemo === FeatureDemo.QR_CODE) {
      trackPosterBuilderGA4Events(GA4EventName.EDITOR_DOWNLOAD_QR, {[GA4EventParamName.USER_PREMIUM_LEVEL]: String(getUserPremiumLevel())});
      dispatch(updateFeatureDemo(FeatureDemo.NONE));
    }
  };

  const startDownload = async (ignorePosterVersionIdIfPresent = false): Promise<void> => {
    if (!meetsGettyRequirements) {
      return;
    }

    trackQRDownloadDemoIfNeeded();

    if (isOpenedInPrintShop) {
      await triggerDownloadForDownloadPage(ignorePosterVersionIdIfPresent);
      return;
    }

    if (isPremiumUser()) {
      if (productsData && productsData?.length > 0 && productsData[0].status === 'out-of-free-downloads') {
        openCheckoutDialogForTrialPremiumUsers(productsData[0].status, (): void => {
          void triggerDownloadForPremiumUser(ignorePosterVersionIdIfPresent);
        });
        return;
      }
      await triggerDownloadForPremiumUser(ignorePosterVersionIdIfPresent);
      return;
    }

    if (selectedProductId === FREEBIE_PRODUCT_ID) {
      await triggerDownloadForFreebieUser(ignorePosterVersionIdIfPresent);
      return;
    }

    await startPaygDownloadFlow();
  };

  const onDownloadToDrive = async (): Promise<void> => {
    await runPreDownloadChecks(async () => {
      trackQRDownloadDemoIfNeeded();
      const oauthUrl = await getOauthUrl();

      if (isPremiumUser()) {
        await openGoogleDriveFolderPicker(oauthUrl, onGoogleDriveFolderSelected);
        return;
      }

      if (isOpenedInPrintShop) {
        await openGoogleDriveFolderPicker(oauthUrl, onGoogleDriveFolderSelectedForDownloadPage);
        return;
      }

      if (selectedProductId === FREEBIE_PRODUCT_ID) {
        await openGoogleDriveFolderPicker(oauthUrl, onGoogleDriveFolderSelectedForFreebie);
      } else {
        await openGoogleDriveFolderPicker(oauthUrl, onGoogleDriveFolderSelectedForPayg);
      }
    });
  };

  const onGoogleDriveFolderSelected = async (): Promise<void> => {
    setIsInitialDownloadInProgress(true);

    try {
      const data = await onDownloadPoster(getDataForDownload(true), getDataAccordingToType());

      if (data.downloadUrl && data.downloadUrl !== '') {
        dispatch(addToCurrentProcessingIds(data.idOrderKey));
        dispatch(
          addOrderStateNew(
            buildOrderStatePayload({
              state: DownloadStateTypes.READY,
              orderId: data.idOrder,
              orderIdKey: data.idOrderKey,
              downloadURL: data.downloadUrl,
              name: getCurrentPosterObject().name,
              downloadResult: data,
              fileType: fileType,
              isGoogleDriveDownload: true,
              thumbnailUrl: data.thumbnailUrl,
            })
          )
        );
        if (!isMyStuffWebModal) {
          dispatch(setCurrentViewingOrderIdKey(data.idOrderKey));
        } else {
          dispatch(closeModal(MODALS_IDS.DOWNLOAD_MODAL));
        }

        const downloadFilename = makeFilenameSafe(getCurrentPosterObject().name, 'poster');
        await saveToGoogleDriveWithSelectedFolder(data.downloadUrl, downloadFilename);
        showEditorNotificationMessageGrowl(window.i18next.t('pmwjs_saved'));
        return;
      }

      dispatch(addToCurrentProcessingIds(data.idOrderKey));
      dispatch(
        addOrderStateNew(
          buildOrderStatePayload({
            state: DownloadStateTypes.DOWNLOADING,
            orderId: data.idOrder,
            orderIdKey: data.idOrderKey,
            downloadResult: data,
            name: getCurrentPosterObject().name,
            fileType: fileType,
            isGoogleDriveDownload: true,
            thumbnailUrl: data.thumbnailUrl,
            isHeavyOrder: data.isHeavyOrder,
          })
        )
      );
      if (!isMyStuffWebModal) {
        dispatch(setCurrentViewingOrderIdKey(data.idOrderKey));
      } else {
        dispatch(closeModal(MODALS_IDS.DOWNLOAD_MODAL));
      }
    } catch (error: unknown) {
      console.error('Google Drive download error:', error);
      addToErrorState();
    } finally {
      setIsInitialDownloadInProgress(false);
      void refreshProductsInContext();
    }
  };

  const onGoogleDriveFolderSelectedForFreebie = async (): Promise<void> => {
    setIsInitialDownloadInProgress(true);

    try {
      const data = await onDownloadPosterForFreebie(getDataForDownload(true), getDataAccordingToType());

      const orderIdKey = getUniqueId();

      if (data.downloadUrl && data.downloadUrl !== '') {
        dispatch(addToCurrentProcessingIds(orderIdKey));
        dispatch(setCurrentViewingOrderIdKey(orderIdKey));
        dispatch(
          addOrderStateNew(
            buildOrderStatePayload({
              state: DownloadStateTypes.READY,
              orderId: orderIdKey,
              orderIdKey: orderIdKey,
              downloadURL: data.downloadUrl,
              name: getCurrentPosterObject().name,
              fileType: fileType,
              thumbnailUrl: data.thumbnailUrl,
              isDownloadPageOrder: false,
              isFreebieDownloadPageOrder: true,
              isGoogleDriveDownload: true,
            })
          )
        );

        const downloadFilename = makeFilenameSafe(getCurrentPosterObject().name, 'poster');
        await saveToGoogleDriveWithSelectedFolder(data.downloadUrl, downloadFilename);
        showEditorNotificationMessageGrowl(window.i18next.t('pmwjs_saved'));
        return;
      }

      if (data.generationKey) {
        dispatch(addToCurrentProcessingIds(orderIdKey));
        dispatch(
          addOrderStateNew(
            buildOrderStatePayload({
              state: DownloadStateTypes.DOWNLOADING,
              orderId: orderIdKey,
              orderIdKey: orderIdKey,
              name: getCurrentPosterObject().name,
              fileType: fileType,
              generationKey: data.generationKey,
              thumbnailUrl: data.thumbnailUrl,
              isDownloadPageOrder: false,
              isFreebieDownloadPageOrder: true,
              isGoogleDriveDownload: true,
            })
          )
        );
        dispatch(setCurrentViewingOrderIdKey(orderIdKey));
        return;
      }

      addToErrorState();
    } catch (error: unknown) {
      console.error('Google Drive download error:', error);
      addToErrorState();
    } finally {
      setIsInitialDownloadInProgress(false);
      void refreshProductsInContext();
    }
  };

  const startFreeDownloadForPaygUserToDrive = async (): Promise<void> => {
    try {
      const data = await onDownloadFreePosterForPaygUser(getDataForDownload(true), getDataAccordingToType());

      if (data.downloadUrl && data.idOrderKey) {
        dispatch(addToCurrentProcessingIds(data.idOrderKey));
        dispatch(
          addOrderStateNew(
            buildOrderStatePayload({
              orderId: data.idOrder ?? '',
              orderIdKey: data.idOrderKey,
              state: DownloadStateTypes.READY,
              downloadURL: data.downloadUrl,
              downloadResult: {
                ...data,
                printShopLink: data.printShopLink,
              },
              name: getCurrentPosterObject().name,
              fileType: fileType,
              isPaygDownload: true,
              thumbnailUrl: data.thumbnailUrl,
              isGoogleDriveDownload: true,
            })
          )
        );
        if (!isMyStuffWebModal) {
          dispatch(setCurrentViewingOrderIdKey(data.idOrderKey));
        } else {
          dispatch(closeModal(MODALS_IDS.DOWNLOAD_MODAL));
        }

        const downloadFilename = makeFilenameSafe(getCurrentPosterObject().name, 'poster');
        await saveToGoogleDriveWithSelectedFolder(data.downloadUrl, downloadFilename);
        showEditorNotificationMessageGrowl(window.i18next.t('pmwjs_saved'));
        setIsInitialDownloadInProgress(false);
        void refreshProductsInContext();
        return;
      }

      if (data.idOrder && data.idOrderKey) {
        dispatch(addToCurrentProcessingIds(data.idOrderKey));
        dispatch(
          addOrderStateNew(
            buildOrderStatePayload({
              orderId: data.idOrder,
              orderIdKey: data.idOrderKey,
              state: DownloadStateTypes.DOWNLOADING,
              name: getCurrentPosterObject().name,
              fileType: fileType,
              isPaygDownload: true,
              thumbnailUrl: data.thumbnailUrl,
              isHeavyOrder: data.isHeavyOrder,
              isGoogleDriveDownload: true,
            })
          )
        );
        if (!isMyStuffWebModal) {
          dispatch(setCurrentViewingOrderIdKey(data.idOrderKey));
        } else {
          dispatch(closeModal(MODALS_IDS.DOWNLOAD_MODAL));
        }
      }
    } catch (err: unknown) {
      console.error('Google Drive download error:', err);
      addToErrorState();
    } finally {
      setIsInitialDownloadInProgress(false);
    }
  };

  const onGoogleDriveFolderSelectedForPayg = async (): Promise<void> => {
    setIsInitialDownloadInProgress(true);

    const {isUserPremium, isPaymentNeeded} = await isPaymentRequired();
    if (isUserPremium) {
      await onGoogleDriveFolderSelected();
      return;
    }

    if (!isPaymentNeeded) {
      await startFreeDownloadForPaygUserToDrive();
      return;
    }

    try {
      const data = await onDownloadPosterUnpurchased(
        {
          hashedId: getCurrentPosterObject().hashedID,
          productId: selectedProductId,
          mode: DownloadPublishMode.DOWNLOAD,
          currencyCode: getCurrencyCodeForProduct(SELECTED_PRODUCT, appleDownloadProducts),
          promotion: '',
          isInAppPurchase: window.PMW.isIOSApp(),
        },
        getDataAccordingToType()
      );

      if (data.orderRegen) {
        await startFreeDownloadForPaygUserToDrive();
        return;
      }

      if (data.checkout) {
        openCheckoutDialogForPAYGUsers(data, true);
        return;
      }
    } catch (error: unknown) {
      console.error('Google Drive download error:', error);
      addToErrorState();
    } finally {
      setIsInitialDownloadInProgress(false);
      void refreshProductsInContext();
    }
  };

  const onDownloadToDropbox = async (): Promise<void> => {
    await runPreDownloadChecks(async () => {
      trackQRDownloadDemoIfNeeded();
      if (isPremiumUser()) {
        await startPremiumDropboxDownload();
        return;
      }

      if (isOpenedInPrintShop) {
        await startDropboxDownloadForDownloadPage();
        return;
      }

      if (selectedProductId === FREEBIE_PRODUCT_ID) {
        await startFreebieDropboxDownload();
        return;
      }

      if (isUserPAYG()) {
        await startPaygDropboxDownload();
        return;
      }
    });
  };

  const startPremiumDropboxDownload = async (): Promise<void> => {
    setIsInitialDownloadInProgress(true);

    try {
      const data = await onDownloadPoster(getDataForDownload(true), getDataAccordingToType());

      if (data.downloadUrl && data.downloadUrl !== '') {
        dispatch(addToDropboxProcessingIds(data.idOrderKey));
        dispatch(
          addOrderStateNew(
            buildOrderStatePayload({
              state: DownloadStateTypes.READY,
              orderId: data.idOrder,
              orderIdKey: data.idOrderKey,
              downloadURL: data.downloadUrl,
              name: getCurrentPosterObject().name,
              downloadResult: data,
              fileType: fileType,
              isDropboxDownload: true,
              thumbnailUrl: data.thumbnailUrl,
            })
          )
        );
        dispatch(setIsViewingDropboxDownloads(true));
        return;
      }

      dispatch(addToDropboxProcessingIds(data.idOrderKey));
      dispatch(addToCurrentProcessingIds(data.idOrderKey));
      dispatch(
        addOrderStateNew(
          buildOrderStatePayload({
            state: DownloadStateTypes.DOWNLOADING,
            orderId: data.idOrder,
            orderIdKey: data.idOrderKey,
            downloadResult: data,
            name: getCurrentPosterObject().name,
            fileType: fileType,
            isDropboxDownload: true,
            thumbnailUrl: data.thumbnailUrl,
            isHeavyOrder: data.isHeavyOrder,
          })
        )
      );
      dispatch(setIsViewingDropboxDownloads(true));
    } catch (error: unknown) {
      console.error('Dropbox download error:', error);
      addToErrorState();
    } finally {
      setIsInitialDownloadInProgress(false);
      void refreshProductsInContext();
    }
  };

  const startFreebieDropboxDownload = async (): Promise<void> => {
    setIsInitialDownloadInProgress(true);

    try {
      const data = await onDownloadPosterForFreebie(getDataForDownload(true), getDataAccordingToType());

      const orderIdKey = getUniqueId();

      if (data.downloadUrl && data.downloadUrl !== '') {
        dispatch(addToDropboxProcessingIds(orderIdKey));
        dispatch(
          addOrderStateNew(
            buildOrderStatePayload({
              state: DownloadStateTypes.READY,
              orderId: orderIdKey,
              orderIdKey: orderIdKey,
              downloadURL: data.downloadUrl,
              name: getCurrentPosterObject().name,
              fileType: fileType,
              isDropboxDownload: true,
              isFreebieDownloadPageOrder: true,
              thumbnailUrl: data.thumbnailUrl,
            })
          )
        );
        dispatch(setIsViewingDropboxDownloads(true));
        return;
      }

      if (data.generationKey) {
        dispatch(addToDropboxProcessingIds(orderIdKey));
        dispatch(addToCurrentProcessingIds(orderIdKey));
        dispatch(
          addOrderStateNew(
            buildOrderStatePayload({
              state: DownloadStateTypes.DOWNLOADING,
              orderId: orderIdKey,
              orderIdKey: orderIdKey,
              name: getCurrentPosterObject().name,
              fileType: fileType,
              generationKey: data.generationKey,
              isDropboxDownload: true,
              isDownloadPageOrder: false,
              isFreebieDownloadPageOrder: true,
              thumbnailUrl: data.thumbnailUrl,
            })
          )
        );
        dispatch(setIsViewingDropboxDownloads(true));
        return;
      }

      addToErrorState();
    } catch (error: unknown) {
      console.error('Dropbox download error:', error);
      addToErrorState();
    } finally {
      setIsInitialDownloadInProgress(false);
      void refreshProductsInContext();
    }
  };

  const startPaygDropboxDownload = async (): Promise<void> => {
    setIsInitialDownloadInProgress(true);

    const {isPaymentNeeded, isUserPremium} = await isPaymentRequired();

    if (isUserPremium) {
      await startPremiumDropboxDownload();
      return;
    }

    if (!isPaymentNeeded) {
      await startFreePaygDropboxDownload();
      return;
    }

    try {
      const data = await onDownloadPosterUnpurchased(
        {
          hashedId: getCurrentPosterObject().hashedID,
          productId: selectedProductId,
          mode: DownloadPublishMode.DOWNLOAD,
          currencyCode: getCurrencyCodeForProduct(SELECTED_PRODUCT, appleDownloadProducts),
          promotion: '',
          isInAppPurchase: window.PMW.isIOSApp(),
        },
        getDataAccordingToType()
      );

      if (data.orderRegen) {
        await startFreePaygDropboxDownload();
        return;
      }

      if (data.checkout) {
        openCheckoutDialogForPAYGUsers(data, false, true);
        return;
      }
    } catch (error: unknown) {
      console.error('Dropbox download error:', error);
      addToErrorState();
    } finally {
      setIsInitialDownloadInProgress(false);
      void refreshProductsInContext();
    }
  };

  const startFreePaygDropboxDownload = async (): Promise<void> => {
    try {
      const data = await onDownloadFreePosterForPaygUser(getDataForDownload(true), getDataAccordingToType());

      if (data.downloadUrl && data.idOrderKey) {
        dispatch(addToDropboxProcessingIds(data.idOrderKey));
        dispatch(
          addOrderStateNew(
            buildOrderStatePayload({
              orderId: data.idOrder ?? '',
              orderIdKey: data.idOrderKey,
              state: DownloadStateTypes.READY,
              downloadURL: data.downloadUrl,
              downloadResult: {
                ...data,
                printShopLink: data.printShopLink,
              },
              name: getCurrentPosterObject().name,
              fileType: fileType,
              isPaygDownload: true,
              isDropboxDownload: true,
              thumbnailUrl: data.thumbnailUrl,
            })
          )
        );
        dispatch(setIsViewingDropboxDownloads(true));
        setIsInitialDownloadInProgress(false);
        void refreshProductsInContext();
        return;
      }

      if (data.idOrder && data.idOrderKey) {
        dispatch(addToDropboxProcessingIds(data.idOrderKey));
        dispatch(addToCurrentProcessingIds(data.idOrderKey));
        dispatch(
          addOrderStateNew(
            buildOrderStatePayload({
              orderId: data.idOrder,
              orderIdKey: data.idOrderKey,
              state: DownloadStateTypes.DOWNLOADING,
              name: getCurrentPosterObject().name,
              fileType: fileType,
              isPaygDownload: true,
              isDropboxDownload: true,
              thumbnailUrl: data.thumbnailUrl,
              isHeavyOrder: data.isHeavyOrder,
            })
          )
        );
        dispatch(setIsViewingDropboxDownloads(true));
      }
    } catch (err: unknown) {
      console.error('Dropbox download error:', err);
      addToErrorState();
    } finally {
      setIsInitialDownloadInProgress(false);
    }
  };

  const startDropboxDownloadForDownloadPage = async (): Promise<void> => {
    if (!downloadPageOptions) {
      throw new Error('Expected downloadPageOptions to be defined');
    }

    setIsInitialDownloadInProgress(true);

    try {
      const data = await onDownloadPosterWithOptions(getDataForDownload(true), getDataAccordingToType());

      const orderIdKey = getUniqueId();

      if (data.downloadUrl && data.downloadUrl !== '') {
        dispatch(addToDropboxProcessingIds(orderIdKey));
        dispatch(
          addOrderStateNew(
            buildOrderStatePayload({
              state: DownloadStateTypes.READY,
              orderId: downloadPageOptions.orderId,
              orderIdKey: orderIdKey,
              downloadURL: data.downloadUrl,
              name: getCurrentPosterObject().name,
              fileType: fileType,
              isDownloadPageOrder: true,
              isFreebieDownloadPageOrder: false,
              isDropboxDownload: true,
              thumbnailUrl: downloadPageOptions.thumbnailURL,
            })
          )
        );
        dispatch(setIsViewingDropboxDownloads(true));
        return;
      }

      if (data.generationKey) {
        dispatch(addToDropboxProcessingIds(orderIdKey));
        dispatch(addToCurrentProcessingIds(orderIdKey));
        dispatch(
          addOrderStateNew(
            buildOrderStatePayload({
              state: DownloadStateTypes.DOWNLOADING,
              orderId: downloadPageOptions.orderId,
              orderIdKey: orderIdKey,
              name: getCurrentPosterObject().name,
              fileType: fileType,
              isDownloadPageOrder: true,
              isFreebieDownloadPageOrder: false,
              isDropboxDownload: true,
              generationKey: data.generationKey,
              thumbnailUrl: downloadPageOptions.thumbnailURL,
            })
          )
        );
        dispatch(setIsViewingDropboxDownloads(true));
        return;
      }

      addToErrorState();
    } catch (error: unknown) {
      console.error('Dropbox download error:', error);
      addToErrorState();
    } finally {
      setIsInitialDownloadInProgress(false);
    }
  };

  const getOauthUrl = async (): Promise<string> => {
    if (oauthUrl.current) {
      return oauthUrl.current;
    }
    oauthUrl.current = (await window.PMW.readLocal('authenticate/getCurrentUserGoogleUploadToken', {
      isIOSAppOrEmbeddedEditor: isIOSAppOrEmbeddedEditor(),
    })) as string;
    if (!oauthUrl.current) {
      throw new Error('Invalid oauthUrl');
    }
    state.current = getStateFromUrl(oauthUrl.current);
    return oauthUrl.current;
  };

  const getSource = (): DownloadDialogSource => {
    if (isOpenedInPrintShop) {
      return DownloadDialogSource.DOWNLOAD_OPTIONS_PAGE;
    }

    if (isOpenedInEditor) {
      return DownloadDialogSource.EDITOR;
    } else {
      return DownloadDialogSource.MYSTUFF;
    }
  };

  const toggleExternalModalOpenState = (state: boolean): void => {
    dispatch(updateExternalModalOpenStateForDownloadPanel(state));
  };

  const triggerGA4event = (): void => {
    trackPosterBuilderGA4Events(GA4EventName.DOWNLOAD_OPTIONS, {[GA4EventParamName.SOURCE]: getSource()});
  };

  const getCurrentPosterObject = (): PosterLoadObject => {
    if (isOpenedInEditor && window.posterEditor?.whiteboard) {
      const whiteboard = window.posterEditor.whiteboard;
      return {
        ...posterObject,
        hashedID: whiteboard.hashedID || posterObject.hashedID,
        name: whiteboard.name || posterObject.name,
        id: whiteboard.id || posterObject.id,
      };
    }
    if (freshPosterObjectRef.current) {
      return freshPosterObjectRef.current;
    }
    return posterObject;
  };

  const runPreDownloadChecks = async (onContinue: () => Promise<void>): Promise<void> => {
    if (!getCurrentPosterObject().hashedID || getCurrentPosterObject().hashedID === '' || !getCurrentPosterObject().id) {
      if (isOpenedInEditor) {
        try {
          toggleExternalModalOpenState(true);
          await window.posterEditor?.whiteboard?.savePoster.save();
        } catch (error) {
          console.log(error);
          addToErrorState();
          return;
        } finally {
          toggleExternalModalOpenState(false);
        }
      } else {
        console.error('Poster hashed ID is missing');
        return;
      }
    }

    if (!isUserLoggedIn() && !isOpenedInPrintShop) {
      toggleExternalModalOpenState(true);
      openAuthenticateModal({
        fbPermissions: '',
        signupSource: 'default',
        onCloseCb: (): void => {
          toggleExternalModalOpenState(false);
        },
        onLoginSuccess: async (): Promise<void> => {
          if (isOpenedInEditor) {
            try {
              await window.posterEditor?.whiteboard?.savePoster.save();
            } catch (error) {
              console.log(error);
              addToErrorState();
              toggleExternalModalOpenState(false);
              return;
            }
          }
          toggleExternalModalOpenState(false);
          await onContinue();
          return Promise.resolve();
        },
      });
      return;
    }

    await onContinue();
  };

  const triggerPreDownloadProcess = (ignorePosterVersionIdIfPresent = false): void => {
    triggerGA4event();
    dispatch(updateDownloadLoadingPopupOpenState(true));
    void startDownload(ignorePosterVersionIdIfPresent);
  };

  const onClickDownload = async (ignorePosterVersionIdIfPresent = false): Promise<void> => {
    await runPreDownloadChecks(async (): Promise<void> => {
      triggerPreDownloadProcess(ignorePosterVersionIdIfPresent);
    });
  };

  const getProductIdFromOptionsForNonPremiumUsers = (): number => {
    return selectedProductId;
  };

  const getProductIdFromOptions = (): number => {
    const data = getDataAccordingToType();
    switch (data.type) {
      case FileTypeOptions.JPG:
      case FileTypeOptions.PNG:
      case FileTypeOptions.GIF:
      case FileTypeOptions.PDF:
        if (selectedPages.length > MAX_PAGES_ALLOWED_FOR_CHEAP_PRODUCT) {
          return EXPENSIVE_PRODUCT_ID;
        }
        return CHEAP_PRODUCT_ID;
      case FileTypeOptions.MP4:
        if (selectedPages.length > MAX_PAGES_ALLOWED_FOR_CHEAP_PRODUCT) {
          return EXPENSIVE_PRODUCT_ID;
        }
        if (!isExpensiveVideoPoster(getCurrentPosterObject())) {
          return CHEAP_PRODUCT_ID;
        }
        if (duration > 20 || videoScale === VideoDesignSizeOptionType.FULL_HD || videoScale === VideoDesignSizeOptionType.UHD_4K) {
          return EXPENSIVE_PRODUCT_ID;
        }
        return CHEAP_PRODUCT_ID;
      default:
        return CHEAP_PRODUCT_ID;
    }
  };

  const onGoogleDriveFolderSelectedForDownloadPage = async (): Promise<void> => {
    if (!downloadPageOptions) {
      throw new Error('Expected downloadPageOptions to be defined');
    }

    setIsInitialDownloadInProgress(true);

    try {
      const currentPosterObject = getCurrentPosterObject();
      const data = await onDownloadPosterWithOptions(getDataForDownload(), getDataAccordingToType());

      const orderIdKey = getUniqueId();

      if (data.downloadUrl && data.downloadUrl !== '') {
        dispatch(addToCurrentProcessingIds(orderIdKey));
        dispatch(setCurrentViewingOrderIdKey(orderIdKey));
        dispatch(
          addOrderStateNew(
            buildOrderStatePayload({
              state: DownloadStateTypes.READY,
              orderId: downloadPageOptions.orderId,
              orderIdKey: orderIdKey,
              downloadURL: data.downloadUrl,
              name: currentPosterObject.name,
              fileType: fileType,
              isDownloadPageOrder: true,
              isFreebieDownloadPageOrder: false,
              isGoogleDriveDownload: true,
              thumbnailUrl: downloadPageOptions.thumbnailURL,
            })
          )
        );

        const downloadFilename = makeFilenameSafe(currentPosterObject.name, 'poster');
        await saveToGoogleDriveWithSelectedFolder(data.downloadUrl, downloadFilename);
        showEditorNotificationMessageGrowl(window.i18next.t('pmwjs_saved'));
        return;
      }

      if (data.generationKey) {
        dispatch(addToCurrentProcessingIds(orderIdKey));
        dispatch(setCurrentViewingOrderIdKey(orderIdKey));
        dispatch(
          addOrderStateNew(
            buildOrderStatePayload({
              state: DownloadStateTypes.DOWNLOADING,
              orderId: downloadPageOptions.orderId,
              orderIdKey: orderIdKey,
              name: currentPosterObject.name,
              fileType: fileType,
              isDownloadPageOrder: true,
              isFreebieDownloadPageOrder: false,
              isGoogleDriveDownload: true,
              generationKey: data.generationKey,
              thumbnailUrl: downloadPageOptions.thumbnailURL,
            })
          )
        );
        return;
      }

      addToErrorState();
    } catch (error: unknown) {
      console.error('Google Drive download error:', error);
      addToErrorState();
    } finally {
      setIsInitialDownloadInProgress(false);
    }
  };

  const triggerDownloadForDownloadPage = async (ignorePosterVersionIdIfPresent = false): Promise<void> => {
    if (!downloadPageOptions) {
      throw new Error('Expected downloadPageOptions to be defined');
    }

    if (downloadPageOptions.ownerId === window.PMW.getUserId() && isPremiumUser()) {
      await triggerDownloadForPremiumUser(ignorePosterVersionIdIfPresent);
      return;
    }

    setIsInitialDownloadInProgress(true);
    const currentPosterObject = getCurrentPosterObject();

    try {
      const data = await onDownloadPosterWithOptions(getDataForDownload(), getDataAccordingToType());

      const orderIdKey = getUniqueId();

      if (data.downloadUrl && data.downloadUrl !== '') {
        dispatch(addToCurrentProcessingIds(orderIdKey));
        dispatch(setCurrentViewingOrderIdKey(orderIdKey));
        dispatch(
          addOrderStateNew(
            buildOrderStatePayload({
              state: DownloadStateTypes.READY,
              orderId: downloadPageOptions.orderId,
              orderIdKey: orderIdKey,
              downloadURL: data.downloadUrl,
              name: currentPosterObject.name,
              fileType: fileType,
              isDownloadPageOrder: true,
              isFreebieDownloadPageOrder: false,
              thumbnailUrl: downloadPageOptions.thumbnailURL,
            })
          )
        );
        const downloadSafePosterName = makeFilenameSafe(currentPosterObject.name, 'poster');
        triggerBrowserDownload(data.downloadUrl, downloadSafePosterName, fileType);
        return;
      }

      if (data.generationKey) {
        dispatch(addToCurrentProcessingIds(orderIdKey));
        dispatch(
          addOrderStateNew(
            buildOrderStatePayload({
              state: DownloadStateTypes.DOWNLOADING,
              orderId: downloadPageOptions.orderId,
              orderIdKey: orderIdKey,
              name: currentPosterObject.name,
              fileType: fileType,
              isDownloadPageOrder: true,
              isFreebieDownloadPageOrder: false,
              thumbnailUrl: downloadPageOptions.thumbnailURL,
              generationKey: data.generationKey,
            })
          )
        );
        dispatch(setCurrentViewingOrderIdKey(orderIdKey));
        return;
      }

      addToErrorState();
    } catch (error: unknown) {
      console.error('Download error:', error);
      addToErrorState();
    } finally {
      setIsInitialDownloadInProgress(false);
    }
  };

  const getPanelHeader = (): ReactElement<any> | undefined => {
    return (
      <DownloadPanelHeader
        id={props.id}
        isOpenedInModal={isOpenedInModal}
        isOpenedInPrintShop={isOpenedInPrintShop}
        isViewingDropboxDownloads={isViewingDropboxDownloads}
        currentViewingOrderIdKey={currentViewingOrderIdKey}
        currentViewingOrder={currentViewingOrder}
        onClose={onClose}
      />
    );
  };

  return (
    <Panel
      panelId={props.id}
      ref={wrapperRef}
      panelHeader={getPanelHeader()}
      className={`${className} download-panel-content-manager ${styles.panel}`}
      panelFooter={
        currentViewingOrderIdKey || isViewingDropboxDownloads ? undefined : (
          <DownloadPanelFooter
            panelId={props.id}
            isOpenedInEditor={isOpenedInEditor}
            showLoading={isInitialDownloadInProgress}
            onClickDownload={(): void => {
              void onClickDownload();
            }}
            onDownloadToDrive={onDownloadToDrive}
            onDownloadToDropbox={onDownloadToDropbox}
            hasTopShadow={bottomShadow}
            isOpenedInPrintShop={isOpenedInPrintShop}
            isOpenedInModal={isOpenedInModal}
          />
        )
      }
      panelBodyClassName={styles.maxHeight}
      panelContent={getPanelContentWrapper()}
    />
  );
}
